// Szolgáltatás-katalógus HÁROMSZINTŰ szerkesztő (service → plan → price) — jobb oldali beúszó FormSheet, KÉT
// tabbal: „Alapadatok" + „Csomagok". Egy komponens két módban: `service == null` → LÉTREHOZÁS (POST
// Idempotency-Key), különben SZERKESZTÉS (a detailt az id-ból töltjük → előtöltés + PUT If-Match/OCC a service
// version-höz). A validálás forrás-igazsága a @atlas/contracts (a resolver a features/service-catalog/form.ts
// séma-gyárán át fut); a mentés-body a nested wire-builder (formToWriteBody). A szerver a végső fal.
//
// STRUKTÚRA: `simple` → egyetlen csomag-árazó blokk közvetlenül (a plan neve a szolgáltatás nevéből → rejtve);
// `package` → csomag-lista (add/edit/remove), csomagonként teljes PlanEditor. A structureType CREATE-nél
// váltható; EDIT-nél package→simple TILTOTT (a BFF is 422-zi) → a select disabled edit-módban.
import { type ServiceCatalogRowSchema } from '@atlas/contracts';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { type Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { PlanEditor } from '@/components/service-catalog/plan-editor';
import { BilingualField } from '@/components/shared/bilingual-field';
import { SelectField, SwitchField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { serviceCatalogKeys } from '@/features/service-catalog/api';
import {
  buildServiceCatalogSchema,
  detailToFormValues,
  emptyPlan,
  EMPTY_SERVICE_CATALOG_FORM,
  formToWriteBody,
  type ServiceCatalogFormValues,
  STATUSES,
  STRUCTURE_TYPES,
  SUPPORT_TEAM_NONE,
} from '@/features/service-catalog/form';
import { api } from '@/lib/api';
import { newIdempotencyKey, useEntityMutation } from '@/lib/use-entity-mutation';

type ServiceRow = z.infer<typeof ServiceCatalogRowSchema>;

export interface ServiceFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő sor (az id-jából töltjük a detailt + az OCC version-t). */
  service: ServiceRow | null;
}

export function ServiceFormSheet({
  open,
  onOpenChange,
  service,
}: ServiceFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = service !== null;
  const [tab, setTab] = useState<'basic' | 'plans'>('basic');

  // Edit: a TELJES detail (service + plans[]+prices[]) — az előtöltés + az OCC version forrása.
  const detailKey = serviceCatalogKeys.detail(service?.id ?? '__none__');
  const detailQ = api.serviceCatalogDetail.useQuery(
    detailKey,
    { params: { id: service?.id ?? '' } },
    { queryKey: detailKey, enabled: open && isEdit },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;

  // Felelős ügyfélszolgálat opciók — a MEGLÉVŐ support-teams lista-végpontról (id + lokalizált név). Jog-kapu:
  // support_teams.view; hiánya (403) esetén üres lista → a mező „nincs kijelölve"-ként működik (best-effort).
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const teamsQ = api.supportTeams.useQuery(
    ['service-catalog-support-teams', lang],
    { query: { lang, limit: 200 } },
    { enabled: open, queryKey: ['service-catalog-support-teams', lang] },
  );
  const supportTeams = teamsQ.data?.status === 200 ? teamsQ.data.body.supportTeams : [];

  const form = useForm<ServiceCatalogFormValues>({
    resolver: zodResolver(buildServiceCatalogSchema(t)) as Resolver<ServiceCatalogFormValues>,
    mode: 'onTouched',
    defaultValues: EMPTY_SERVICE_CATALOG_FORM,
  });
  const { reset, handleSubmit, watch, setValue, getValues, setError } = form;

  // Előtöltés: create → üres; edit → a betöltött detail. A csomagokat NYELV-FÜGGŐ ABC-sorrendbe rendezzük
  // (a `package`-szerkesztő blokkjai így nem „össze-vissza", hanem név szerint — locale-tudatos collation).
  useEffect(() => {
    if (isEdit) {
      if (detail) {
        const values = detailToFormValues(detail);
        const lang = i18n.language;
        const nameOf = (p: (typeof values.plans)[number]): string =>
          (lang === 'en' ? p.nameEn : p.nameHu) || p.nameHu || p.nameEn || '';
        values.plans = [...values.plans].sort((a, b) => nameOf(a).localeCompare(nameOf(b), lang));
        reset(values);
      }
    } else {
      reset(EMPTY_SERVICE_CATALOG_FORM);
    }
    setTab('basic');
  }, [isEdit, detail, reset, open, i18n.language]);

  const structureType = watch('structureType');
  const plansArray = useFieldArray({ control: form.control, name: 'plans' });

  // structureType-váltás (CSAK create): simple → egyetlen csomagra csonkít; package → megtartja (>=1).
  const prevStructure = useRef(structureType);
  useEffect(() => {
    if (isEdit) return;
    if (structureType === prevStructure.current) return;
    prevStructure.current = structureType;
    if (structureType === 'simple') {
      const first = getValues('plans')[0] ?? emptyPlan();
      setValue('plans', [first], { shouldValidate: false });
    }
  }, [structureType, isEdit, getValues, setValue]);

  const [openPlan, setOpenPlan] = useState<number | null>(0);

  const mutation = useEntityMutation<ServiceCatalogFormValues>({
    mutationFn: (values) => {
      const body = formToWriteBody(values);
      return isEdit
        ? api.serviceCatalogUpdate.mutation({
            params: { id: service.id },
            body,
            headers: { 'if-match': String(detail?.version ?? service.version) },
          })
        : api.serviceCatalogCreate.mutation({
            body,
            headers: { 'idempotency-key': newIdempotencyKey() },
          });
    },
    invalidateKeys: [serviceCatalogKeys.lists(), detailKey],
    onFieldErrors: (field, messages) => {
      // A szerver-oldali service-szintű mező-hibák a formra terelve (a nested plan/ár-hibákat a toast fedi).
      const map: Record<string, keyof ServiceCatalogFormValues> = {
        name: 'nameHu',
        nameHu: 'nameHu',
        nameEn: 'nameEn',
        structureType: 'structureType',
        status: 'status',
      };
      const target = map[field];
      if (target) setError(target, { message: messages.join(' ') });
    },
    messages: {
      success: isEdit ? t('toast:serviceCatalog.updated') : t('toast:serviceCatalog.created'),
      conflict: t('toast:serviceCatalog.conflict'),
      forbidden: t('toast:serviceCatalog.forbidden'),
      validation: t('toast:serviceCatalog.validation'),
      error: t('toast:serviceCatalog.error'),
    },
  });

  const onSubmit = handleSubmit(
    (values) => {
      mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
    },
    () => {
      // Ha a hibák a „Csomagok" tabon vannak, oda navigálunk, hogy a felhasználó lássa őket.
      const errs = form.formState.errors;
      const basicOk = !errs.nameHu && !errs.nameEn && !errs.descriptionHu && !errs.descriptionEn;
      if (basicOk && errs.plans) setTab('plans');
    },
  );

  const structureOptions = STRUCTURE_TYPES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.structureLabel.${v}`),
  }));
  const statusOptions = STATUSES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.statusLabel.${v}`),
  }));

  // Felelős ügyfélszolgálat opciók: „nincs" sentinel + a betöltött csapatok. Ha az edit-módban előtöltött
  // supportTeamId nincs a listában (törölt csapat / hiányzó jog), egy fallback-opcióval megtartjuk (nem vész el).
  const currentTeamId = watch('supportTeamId');
  const supportTeamOptions = [
    { value: SUPPORT_TEAM_NONE, label: t('serviceCatalog.fields.supportTeamNone') },
    ...supportTeams.map((team) => ({ value: team.id, label: team.name })),
  ];
  if (
    currentTeamId !== SUPPORT_TEAM_NONE &&
    !supportTeams.some((team) => team.id === currentTeamId)
  ) {
    supportTeamOptions.push({
      value: currentTeamId,
      label: t('serviceCatalog.fields.supportTeamUnknown'),
    });
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_SERVICE_CATALOG_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('serviceCatalog.editTitle') : t('serviceCatalog.createTitle')}
      description={isEdit ? service.name : t('serviceCatalog.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('serviceCatalog.cancel')}
          </Button>
          <Button
            type="submit"
            variant="brand"
            disabled={mutation.isPending || (isEdit && !detail)}
          >
            {mutation.isPending ? t('serviceCatalog.saving') : t('serviceCatalog.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'basic' | 'plans')}>
          <TabsList className="w-full">
            <TabsTrigger value="basic">{t('serviceCatalog.tabs.basic')}</TabsTrigger>
            <TabsTrigger value="plans">{t('serviceCatalog.tabs.plans')}</TabsTrigger>
          </TabsList>

          {/* ── Alapadatok ── */}
          <TabsContent value="basic" className="space-y-4 pt-2">
            <BilingualField<ServiceCatalogFormValues>
              variant="input"
              label={t('serviceCatalog.fields.name')}
              nameHu="nameHu"
              nameEn="nameEn"
              required
            />
            <BilingualField<ServiceCatalogFormValues>
              variant="textarea"
              label={t('serviceCatalog.fields.description')}
              nameHu="descriptionHu"
              nameEn="descriptionEn"
              rows={2}
            />
            <SelectField<ServiceCatalogFormValues>
              name="structureType"
              label={t('serviceCatalog.fields.structureType')}
              required
              options={structureOptions}
              disabled={isEdit}
              hint={isEdit ? t('serviceCatalog.fields.structureTypeLockedHint') : ''}
            />
            <SelectField<ServiceCatalogFormValues>
              name="status"
              label={t('serviceCatalog.fields.status')}
              required
              options={statusOptions}
            />
            <SelectField<ServiceCatalogFormValues>
              name="supportTeamId"
              label={t('serviceCatalog.fields.supportTeam')}
              options={supportTeamOptions}
              hint={t('serviceCatalog.fields.supportTeamHint')}
            />
            <SwitchField<ServiceCatalogFormValues>
              name="showInTicketDropdown"
              label={t('serviceCatalog.fields.showInTicketDropdown')}
              hint={t('serviceCatalog.fields.showInTicketDropdownHint')}
            />
            {/* A legacy „Dashboard chartokon csoportosítva" jelölő: az érték eddig is KÖRBEJÁRT a formon és a
                BFF whitelist-merge-én, csak KAPCSOLÓJA nem volt — vagyis nem lehetett bekapcsolni. */}
            <SwitchField<ServiceCatalogFormValues>
              name="groupedInCharts"
              label={t('serviceCatalog.fields.groupedInCharts')}
              hint={t('serviceCatalog.fields.groupedInChartsHint')}
            />
          </TabsContent>

          {/* ── Csomagok ── */}
          <TabsContent value="plans" className="space-y-4 pt-2">
            {structureType === 'simple' ? (
              // simple → EGY csomag-árazó blokk közvetlenül (a plan neve rejtve).
              <PlanEditor form={form} index={0} showName={false} />
            ) : (
              <div className="space-y-3">
                {plansArray.fields.map((planField, index) => {
                  const expanded = openPlan === index;
                  const planName = watch(`plans.${index}.nameHu`) || watch(`plans.${index}.nameEn`);
                  const hasPlanError = Boolean(form.formState.errors.plans?.[index]);
                  return (
                    <div key={planField.id} className="border-border rounded-lg border">
                      <div className="flex items-center justify-between gap-2 px-4 py-3">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          onClick={() => setOpenPlan(expanded ? null : index)}
                          aria-expanded={expanded}
                        >
                          {expanded ? (
                            <ChevronUpIcon className="text-muted-foreground size-4 shrink-0" />
                          ) : (
                            <ChevronDownIcon className="text-muted-foreground size-4 shrink-0" />
                          )}
                          <span className="truncate text-sm font-medium">
                            {planName || t('serviceCatalog.plan.untitled', { index: index + 1 })}
                          </span>
                          {hasPlanError && (
                            <span className="bg-destructive size-1.5 shrink-0 rounded-full" />
                          )}
                        </button>
                        {plansArray.fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('serviceCatalog.plan.remove')}
                            onClick={() => {
                              plansArray.remove(index);
                              setOpenPlan(null);
                            }}
                          >
                            <TrashIcon className="text-destructive h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {expanded && (
                        <>
                          <Divider spacing="none" />
                          <div className="p-4">
                            <PlanEditor form={form} index={index} showName />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    plansArray.append(emptyPlan());
                    setOpenPlan(plansArray.fields.length);
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('serviceCatalog.plan.add')}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Form>
    </FormSheet>
  );
}
